import AddressAutoComplete from "@/components/AddressAutoComplete";
import type { Estimation } from "@/interfaces/estimation.interface";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

interface AddressFieldProps {
  label: string;
  name: keyof Estimation;
  value?: string;
  icon?: ReactNode;
  placeholder?: ReactNode | string;
  estimation: Estimation | undefined;
  updateEstimationDetails: (_estimation: Estimation, field: keyof Estimation, clientOnly?: boolean) => Promise<boolean>;
}

export const AddressField = ({ name, icon, label, value, estimation, placeholder, updateEstimationDetails }: AddressFieldProps) => {
  const [edit, setEdit] = useState(false);
  const [error, setError] = useState<string>();
  const [address, setAddress] = useState<string | undefined>(value);

  useEffect(() => {
    setAddress(value);
  }, [value]);

  const toggleEdit = useCallback(() => {
    setEdit((prev) => !prev);
  }, []);

  const updateAddress = useCallback(
    async (_value: string, addressComponents: google.maps.GeocoderAddressComponent[]) => {
      if (value === _value) {
        toggleEdit();
        return;
      }

      if (!estimation) return;

      const newEstimation = structuredClone(estimation);

      newEstimation[name] = _value as never;
      newEstimation.propertyAddressComponents = JSON.stringify(addressComponents);

      updateEstimationDetails(newEstimation, name);
      updateEstimationDetails(newEstimation, "propertyAddressComponents");

      toggleEdit();
    },
    [estimation, name, toggleEdit, updateEstimationDetails, value],
  );

  const onAddressChange = useCallback((value: string) => {
    setAddress(value);
  }, []);

  const onAddressComplete = useCallback(
    (value: string, ac?: google.maps.GeocoderAddressComponent[]) => {
      const addressComponents = ac || [];

      // If empty, clear and done
      if (!value) {
        updateAddress(value, addressComponents);
        setError(undefined);
        return;
      }

      // If user typed but didn't select, AddressAutoComplete shows error
      // Don't save, but no need for duplicate error here
      if (addressComponents.length === 0) {
        return;
      }

      // Valid selection - check postal code requirement
      if (!addressComponents.some((a: google.maps.GeocoderAddressComponent) => a.types.includes("postal_code"))) {
        setError("Please include zip-code in the Property address.");
      } else {
        updateAddress(value, addressComponents);
        setError(undefined);
      }
    },
    [updateAddress],
  );

  return (
    <div className={`grid grid-cols-3`}>
      <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
        <label>{label}</label>
      </div>

      <div autoFocus className="col-span-2">
        {edit ? (
          <div>
            <AddressAutoComplete
              className="!h-7 max-h-7 w-[200px] max-w-[200px] rounded-lg border !border-gray-300 !pl-2 !text-sm shadow-sm"
              placeholder={placeholder as string}
              value={address}
              onChange={onAddressChange}
              onComplete={onAddressComplete}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        ) : (
          <div className={`flex w-full flex-row items-center space-x-2`}>
            <div>{icon}</div>
            <p onClick={toggleEdit} className="min-w-[180px] cursor-pointer text-sm text-[#9ca3af]">
              {value?.length ? value : placeholder}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
